import { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  Grid,
  Paper,
  Divider,
  Chip,
  InputAdornment,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import EventIcon from "@mui/icons-material/Event";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamIcon from "@mui/icons-material/Videocam";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 毛玻璃效果的卡片 - 移除hover效果
const GlassCard = styled(Paper)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "24px",
  padding: theme.spacing(4),
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 24,
    pointerEvents: "none",
  },
  // 移除hover样式
}));

// 毛玻璃效果的输入框，所有文本白色，helperText白色
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "12px",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#fff",
    fontSize: "0.9rem",
    "&.Mui-focused": {
      color: "#fff",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    fontSize: "0.95rem",
  },
  "& .MuiFormHelperText-root": {
    color: "#fff",
    fontSize: "0.85rem",
    marginLeft: 0,
  },
  marginBottom: theme.spacing(3),
}));

// 毛玻璃效果的按钮，所有文字白色，禁用状态也白色，无hover效果
const GlassButton = styled(Button)(({ theme, color = "#2196f3" }) => ({
  background: `linear-gradient(135deg, ${color}25, ${color}10)`,
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  borderRadius: "8px",
  color: "#fff",
  padding: theme.spacing(1, 3),
  border: `1px solid ${color}20`,
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${color}25, ${color}10)`,
    transform: "none",
    boxShadow: "none",
  },
  // 禁用时文本也为白色
  "&:disabled": {
    background: "rgba(255, 255, 255, 0.08)",
    color: "#fff",
  },
}));

// 毛玻璃效果的表格，所有文字白色，无hover效果，透明背景
const GlassTable = styled(Table)(({ theme }) => ({
  "& .MuiTableCell-root": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    background: "transparent",
    color: "#fff", // 主要白色文字
    fontSize: "0.95rem",
    padding: theme.spacing(2),
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    background: "transparent",
    fontWeight: 600,
    fontSize: "1rem",
    color: "#fff", // 表头白色
  },
  // 移除hover行高亮
}));

// 毛玻璃效果的对话框
const GlassDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "rgba(50, 50, 50, 0.96)",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    color: "#fff", // 内容区白色文字
  },
  "& .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiDialogActions-root, & .MuiDialogContentText-root":
    {
      color: "#fff",
    },
}));

// 分组标题
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#fff",
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

// 标题容器
const TitleContainer = styled(Box)(({ theme }) => ({
  height: "40px",
  display: "flex",
  alignItems: "center",
}));

// 加载中遮罩
const LoadingBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  backdropFilter: "blur(4px)",
}));

const WorshipManagement = () => {
  const [worships, setWorships] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [worshipDate, setWorshipDate] = useState("");
  const [displayDate, setDisplayDate] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [currentId, setCurrentId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // 获取所有崇拜
  const fetchWorships = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/worship`);
      setWorships(response.data);
    } catch (err) {
      console.error("获取崇拜数据失败:", err);
      setError("获取崇拜数据失败，请稍后再试");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorships();
  }, []);

  // 重置表单
  const resetForm = () => {
    setTitle("");
    setDescription("");
    setWorshipDate("");
    setDisplayDate("");
    setVideoUrl("");
    setCurrentId(null);
    setIsEditing(false);
  };

  // 处理表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 表单验证
    if (!title) {
      setError("标题不能为空");
      return;
    }
    if (!worshipDate) {
      setError("崇拜日期不能为空");
      return;
    }
    if (!displayDate) {
      setError("显示日期不能为空");
      return;
    }
    if (!description) {
      setError("描述不能为空");
      return;
    }

    const worshipData = {
      title,
      date: worshipDate,
      displayDate,
      description,
      videoUrl,
    };

    try {
      setLoading(true);
      setError("");

      if (isEditing) {
        // 更新崇拜
        await axios.put(`${API_BASE_URL}/worship/${currentId}`, worshipData);
        setSuccess("崇拜更新成功！");
      } else {
        // 创建新崇拜
        await axios.post(`${API_BASE_URL}/worship`, worshipData);
        setSuccess("崇拜创建成功！");
      }

      // 重新获取崇拜列表
      fetchWorships();
      resetForm();
    } catch (err) {
      setError(isEditing ? "更新崇拜失败" : "创建崇拜失败");
      console.error(isEditing ? "更新崇拜失败:" : "创建崇拜失败:", err);
    } finally {
      setLoading(false);
    }
  };

  // 编辑崇拜
  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/worship/${id}`);
      const worship = response.data;

      setTitle(worship.title);
      setDescription(worship.description);
      // 格式化日期为 YYYY-MM-DDThh:mm
      const date = new Date(worship.date);
      const formattedDate = date.toISOString().slice(0, 16);
      setWorshipDate(formattedDate);
      setDisplayDate(worship.displayDate);
      setVideoUrl(worship.videoUrl || "");
      setCurrentId(worship.id);
      setIsEditing(true);

      // 滚动到表单顶部
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("获取崇拜详情失败");
      console.error("获取崇拜详情失败:", err);
    } finally {
      setLoading(false);
    }
  };

  // 打开删除确认对话框
  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  // 关闭删除确认对话框
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };

  // 删除崇拜
  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/worship/${deleteId}`);
      setSuccess("崇拜删除成功！");
      fetchWorships();
    } catch (err) {
      setError("删除崇拜失败");
      console.error("删除崇拜失败:", err);
    } finally {
      setLoading(false);
      handleCloseDeleteDialog();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontWeight: 500,
          color: "#fff",
          textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          position: "relative",
          zIndex: 1,
        }}
      >
        崇拜管理
      </Typography>

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            backgroundColor: "rgba(237, 247, 237, 0.6)",
            border: "1px solid rgba(46, 125, 50, 0.1)",
            borderRadius: "8px",
          }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            "& .MuiAlert-message": { color: "#fff" },
            "& .MuiAlert-icon": { color: "#fff" },
            backgroundColor: "rgba(244, 67, 54, 0.2)",
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 表单部分 */}
        <Grid item xs={12} md={4}>
          <GlassCard>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                }}
              >
                {isEditing ? "编辑崇拜" : "添加新崇拜"}
              </Typography>

              <form onSubmit={handleSubmit}>
                <GlassTextField
                  label="崇拜标题"
                  type="text"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  error={error === "标题不能为空"}
                  helperText={error === "标题不能为空" ? error : ""}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon
                          sx={{
                            color: "#fff",
                            position: "relative",
                            zIndex: 1,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  label="崇拜日期时间"
                  type="datetime-local"
                  fullWidth
                  value={worshipDate}
                  onChange={(e) => setWorshipDate(e.target.value)}
                  error={error === "崇拜日期时间不能为空"}
                  helperText={
                    error === "崇拜日期时间不能为空" ? error : "用于系统排序"
                  }
                  InputLabelProps={{ shrink: true }}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EventIcon
                          sx={{
                            color: "#fff",
                            position: "relative",
                            zIndex: 1,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  label="显示日期"
                  type="text"
                  fullWidth
                  value={displayDate}
                  onChange={(e) => setDisplayDate(e.target.value)}
                  error={error === "显示日期不能为空"}
                  helperText={
                    error === "显示日期不能为空"
                      ? error
                      : "例如: 2025-01-12 星期日"
                  }
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon
                          sx={{
                            color: "#fff",
                            position: "relative",
                            zIndex: 1,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  label="崇拜描述"
                  type="text"
                  fullWidth
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  error={error === "描述不能为空"}
                  helperText={error === "描述不能为空" ? error : ""}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon sx={{ color: "#fff" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  label="YouTube 视频链接"
                  type="text"
                  fullWidth
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  helperText="例如: https://www.youtube.com/embed/videoId"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VideocamIcon sx={{ color: "#fff" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <GlassButton
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon sx={{ color: "#fff" }} />}
                    disabled={loading}
                    color="#2196f3"
                  >
                    {isEditing ? "更新崇拜" : "添加崇拜"}
                  </GlassButton>

                  {isEditing && (
                    <GlassButton
                      variant="outlined"
                      onClick={resetForm}
                      color="#4caf50"
                    >
                      取消编辑
                    </GlassButton>
                  )}
                </Box>
              </form>
            </Box>
          </GlassCard>
        </Grid>

        {/* 崇拜列表部分 */}
        <Grid item xs={12} md={8}>
          <GlassCard>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                }}
              >
                崇拜列表
              </Typography>

              {worships.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#fff",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    暂无崇拜数据
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <GlassTable>
                    <TableHead>
                      <TableRow>
                        <TableCell>标题</TableCell>
                        <TableCell>日期</TableCell>
                        <TableCell>操作</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {worships.map((worship) => (
                        <TableRow key={worship.id}>
                          <TableCell>{worship.title}</TableCell>
                          <TableCell>{worship.displayDate}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(worship.id)}
                              sx={{
                                color: "#fff",
                                position: "relative",
                                zIndex: 1,
                              }}
                            >
                              <EditIcon
                                fontSize="small"
                                sx={{ color: "#fff" }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(worship.id)}
                              sx={{
                                color: "#fff",
                                position: "relative",
                                zIndex: 1,
                              }}
                            >
                              <DeleteIcon
                                fontSize="small"
                                sx={{ color: "#fff" }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </GlassTable>
                </TableContainer>
              )}
            </Box>
          </GlassCard>
        </Grid>
      </Grid>

      {/* 删除确认对话框 */}
      <GlassDialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ color: "#fff" }}>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#fff" }}>
            您确定要删除这个崇拜记录吗？此操作无法撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} sx={{ color: "#fff" }}>
            取消
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            autoFocus
            sx={{ color: "#fff" }}
          >
            删除
          </Button>
        </DialogActions>
      </GlassDialog>

      {/* 加载中遮罩 */}
      <LoadingBackdrop open={loading}>
        <CircularProgress sx={{ color: "#fff" }} />
      </LoadingBackdrop>
    </Box>
  );
};

export default WorshipManagement;
